// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { AdvisorAgencyCode, EffectiveAgencyCode } from '../types/agencyCode';

// Use native fetch with credentials (same pattern as brokerageConfigApi)
const BASE = '/api';

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw Object.assign(new Error(`${response.status}: ${body}`), { response });
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

export const agencyCodeApi = {
  getAgencyCodes: (advisorId: string): Promise<AdvisorAgencyCode[]> =>
    fetchJson(`${BASE}/advisors/${advisorId}/agency-codes`),

  createAgencyCode: (advisorId: string, providerId: string, agencyCode: string): Promise<AdvisorAgencyCode> =>
    fetchJson(`${BASE}/advisors/${advisorId}/agency-codes`, {
      method: 'POST',
      body: JSON.stringify({ providerId, agencyCode }),
    }),

  updateAgencyCode: (advisorId: string, providerId: string, agencyCode: string): Promise<AdvisorAgencyCode> =>
    fetchJson(`${BASE}/advisors/${advisorId}/agency-codes/${providerId}`, {
      method: 'PUT',
      body: JSON.stringify({ agencyCode }),
    }),

  deleteAgencyCode: (advisorId: string, providerId: string): Promise<void> =>
    fetchJson(`${BASE}/advisors/${advisorId}/agency-codes/${providerId}`, {
      method: 'DELETE',
    }),

  getEffectiveAgencyCode: (advisorId: string, providerId: string): Promise<EffectiveAgencyCode> =>
    fetchJson(`${BASE}/advisors/${advisorId}/agency-codes/${providerId}/effective`),
};
